import React, { useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'

// The name prompt for the Tables tree's Rename, Duplicate and New folder
// (#1534). It replaced window.prompt, which a page tool could not drive and
// which stopped the page while it was open.

export interface TocNameDialogProps {
  open: boolean
  title: string
  label: string
  initialValue?: string
  submitLabel: string
  /** Rename: the name must differ from the initial value. */
  requireChange?: boolean
  /** Runs the change. A thrown error keeps the dialog open; the caller has already shown it. */
  onSubmit: (name: string) => Promise<void>
  onClose: () => void
}

const TocNameDialog: React.FC<TocNameDialogProps> = ({ open, title, label, initialValue = '', submitLabel, requireChange = false, onSubmit, onClose }) => {
  // The caller mounts one dialog per request (a key), so the value starts
  // from that request's initial value.
  const [value, setValue] = useState(initialValue)
  const [saving, setSaving] = useState(false)

  const trimmed = value.trim()
  const unchanged = requireChange && trimmed === initialValue.trim()

  const handleSubmit = async () => {
    if (!trimmed || unchanged || saving) return
    setSaving(true)
    try {
      await onSubmit(trimmed)
      onClose()
    } catch {
      // The caller showed the error; keep the dialog so the name can be fixed.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          margin="dense"
          label={label}
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void handleSubmit()
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="info" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="info" variant="contained" onClick={() => void handleSubmit()} disabled={!trimmed || unchanged || saving}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TocNameDialog
